import { useEffect, useRef, useState } from 'react';

import type { VoiceRecorderActions, VoiceRecorderHookProps, VoiceRecorderState } from '.';

export function useVoiceRecorder(voiceRecorderProps: VoiceRecorderHookProps = {}): [VoiceRecorderState, VoiceRecorderActions] {
  const {
    onRecordingPause,
    onRecordingStop,
    mediaRecorderOptions = {
      mimeType: 'audio/webm',
      bitsPerSecond: 12000,
      audioConstantBitRate: true,
    },
  } = voiceRecorderProps;
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [recordingTime, setRecordingTime] = useState<number>(0);
  const [analyserNode, setAnalyserNode] = useState<AnalyserNode | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const recordedBlobRef = useRef<Blob | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const formatTime = (seconds: number): string => {
    const mins: number = Math.floor(seconds / 60);
    const secs: number = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const cleanupResources = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track: MediaStreamTrack) => track.stop());
      streamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    analyserRef.current = null;
    mediaRecorderRef.current = null;
  };

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setRecordingTime((prev) => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startRecording = async () => {
    try {
      recordedBlobRef.current = null;
      chunksRef.current = [];

      const stream: MediaStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      streamRef.current = stream;

      audioContextRef.current = new AudioContext();
      const source: MediaStreamAudioSourceNode = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      source.connect(analyserRef.current);
      setAnalyserNode(analyserRef.current);

      mediaRecorderRef.current = new MediaRecorder(stream, mediaRecorderOptions);

      mediaRecorderRef.current.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      mediaRecorderRef.current.onstop = async () => {
        if (chunksRef.current.length === 0) return;
        recordedBlobRef.current = new Blob(chunksRef.current, {
          type: 'audio/webm',
        });

        onRecordingStop?.(recordedBlobRef.current);
        cleanupResources();
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);
      startTimer();
    } catch (error) {
      console.error('Error starting recording:', error);
      cleanupResources();
    }
  };

  const pauseRecording = () => {
    if (!mediaRecorderRef.current || !isRecording) return;

    if (isPaused) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      startTimer();
    } else {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      stopTimer();

      if (recordedBlobRef.current && onRecordingPause) {
        onRecordingPause(new Blob([recordedBlobRef.current], { type: 'audio/webm' }));
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
    stopTimer();

    setIsRecording(false);
    setIsPaused(false);
    setRecordingTime(0);
    setAnalyserNode(null);
  };

  useEffect(() => {
    return () => {
      cleanupResources();
    };
  }, [cleanupResources]);

  const state: VoiceRecorderState = {
    isRecording,
    isPaused,
    recordingTime,
    analyserNode,
  };

  const actions: VoiceRecorderActions = {
    startRecording,
    pauseRecording,
    stopRecording,
    formatTime,
  };

  return [state, actions];
}
